// FTEDN Creator — Dashboard (real creator state)
import { useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useFlag } from '../../hooks/useFlag';
import { useBetaStore } from '../../stores/beta.store';
import { useProductStore } from '../../stores/product.store';
import { useUserStore } from '../../stores/user.store';
import './DashboardPage.css';

function TestTube({ fill = 60, color = '#6DC05E' }: { fill?: number; color?: string }) {
  return (
    <svg className="tube-svg" viewBox="0 0 28 52" fill="none">
      <rect x="7" y="0" width="14" height="4" rx="1" fill="rgba(255,255,255,0.15)" />
      <rect x="9" y="4" width="10" height="38" rx="5" fill="rgba(255,255,255,0.06)"
        stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      <rect x="9" y={4 + 38 * (1 - fill / 100)} width="10"
        height={38 * fill / 100} rx="5" fill={color} opacity="0.7" />
      <ellipse cx="14" cy="46" rx="8" ry="5" fill="rgba(255,255,255,0.04)" />
    </svg>
  );
}

export function DashboardPage() {
  const { displayName } = useAuth();
  const { profile } = useUserStore();
  const { products, fetchProducts } = useProductStore();
  const { programs, applications, fetchPrograms, fetchMyApplications } = useBetaStore();
  const streaksEnabled = useFlag('mechanic.streaks.enabled') === true;

  useEffect(() => {
    fetchProducts();
    fetchPrograms();
    fetchMyApplications();
  }, [fetchProducts, fetchPrograms, fetchMyApplications]);

  const acceptedApplications = applications.filter(app => app.status === 'accepted');
  const programById = new Map(programs.map(program => [program.id, program]));
  const trials = acceptedApplications.map((application) => {
    const program = programById.get(application.programId);
    const product = products.find(p => p.id === program?.productId || p.id === application.programId);
    const phase = program?.phase ?? 1;
    const totalPhases = program?.totalPhases ?? 4;
    return {
      id: application.id,
      name: product?.name ?? program?.name ?? 'Beta product',
      serial: application.id,
      phase,
      totalPhases,
      fill: Math.min(100, (phase / totalPhases) * 100),
      status: application.shipment?.status ? String(application.shipment.status) : 'accepted',
    };
  });

  const xp = profile?.xp ?? 0;
  const level = profile?.level ?? Math.floor(xp / 500) + 1;
  const levelBase = Math.max(0, (level - 1) * 500);
  const nextLevelXp = level * 500;
  const xpIntoLevel = Math.max(0, xp - levelBase);
  const xpToNext = Math.max(0, nextLevelXp - xp);
  const xpPercent = Math.min(100, Math.round((xpIntoLevel / 500) * 100));
  const tierName = profile?.tierName ?? 'Seedling';

  return (
    <div className="dashboard page-enter">
      <div className="dash-hero">
        <div className="dash-blob" />

        <div className="dash-hero-top">
          <div className="dash-logo-mark">
            <div>
              <span className="dash-logo-f">f</span>
              <span className="dash-logo-dot">.</span>
            </div>
            <div className="dash-greeting">
              <span className="dash-tier-label">{tierName} · Lv.{level}</span>
              <h1 className="dash-user-name">Hi, {displayName}</h1>
            </div>
          </div>

          <div className="dash-hero-actions">
            <button className="dash-action-btn" id="dash-search-btn" aria-label="Search">🔍</button>
            <button className="dash-action-btn" id="dash-notif-btn" aria-label="Alerts">
              🧪
              {trials.length > 0 && <span className="dash-action-dot" />}
            </button>
          </div>
        </div>

        <div className="eeduner-status">
          <div className="eeduner-label">Eeduner Status</div>
          <div className="eeduner-title">
            {tierName} · <em>lv.{level}</em>
          </div>

          <div className="eeduner-xp-row">
            <span>{xp.toLocaleString()} XP</span>
            <span>{xpToNext.toLocaleString()} to next level</span>
          </div>
          <div className="eeduner-xp-bar">
            <div className="eeduner-xp-fill" style={{ width: `${xpPercent}%` }} />
          </div>

          <div className="eeduner-badges">
            <span className="eeduner-badge">
              <span className="badge-icon">🧪</span> {trials.length} Active Trial{trials.length === 1 ? '' : 's'}
            </span>
            {streaksEnabled && (
              <span className="eeduner-badge">
                <span className="badge-icon">🔥</span> {profile?.streak ?? 0}D Streak
              </span>
            )}
            <span className="eeduner-badge">
              <span className="badge-icon">⭐</span> {profile?.reviewCount ?? 0} Reviews
            </span>
          </div>
        </div>
      </div>

      <div className="trials-section">
        <div className="section-header">
          <div>
            <div className="section-overline">On Your Bench</div>
            <div className="section-title">
              Active trials <span className="count">· {trials.length}</span>
            </div>
          </div>
          <a href="/lab" className="section-link">See all</a>
        </div>

        {trials.length === 0 ? (
          <div className="task-item">
            <div className="task-content">
              <div className="task-title">No active trials</div>
              <div className="task-sub">Accepted beta products will show here when they are assigned.</div>
            </div>
          </div>
        ) : trials.map((trial, i) => (
          <div key={trial.id} className="trial-card" style={{ animationDelay: `${(i + 1) * 0.1}s` }}>
            <div className="tube-icon">
              <TestTube fill={trial.fill} />
              <span className="tube-phase-label">P{trial.phase}</span>
            </div>

            <div className="trial-info">
              <div className="trial-name">
                {trial.name}
                <span className="trial-serial">{trial.serial}</span>
              </div>

              <div className="phase-row">
                <span className="phase-label">Phase</span>
                <div className="phase-bar-track">
                  {Array.from({ length: trial.totalPhases }).map((_, j) => (
                    <div key={j} className={`phase-seg ${j < trial.phase ? 'filled' : ''}`} />
                  ))}
                </div>
                <span className="phase-count">{trial.phase}/{trial.totalPhases}</span>
              </div>

              <div className="trial-meta">
                <span className="meta-chip meta-chip--day">{trial.status}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="tasks-section">
        <div className="section-header">
          <div>
            <div className="section-overline">Today</div>
            <div className="section-title">
              Lab Tasks <span className="count">· 0 due</span>
            </div>
          </div>
        </div>

        <div className="task-item">
          <div className="task-content">
            <div className="task-title">No lab tasks due</div>
            <div className="task-sub">Check-ins and review tasks will appear here when the API returns them.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
